import { useEffect, useState, type ReactNode } from 'react';
import { ThemeToggle } from './theme.tsx';
import { Wordmark } from './Wordmark.tsx';

/**
 * The sign-in page — RX-AUTH-08.
 *
 * One copy, shared by all three consoles. It was three identical files, and
 * the moment a second way in was added that would have been three places to
 * get it right.
 *
 * ── Two doors, no password ─────────────────────────────────────────────────
 * A code to a phone number, or a code to an email address. Which are offered
 * is the control plane's decision, read from `/api/auth-methods`, because it
 * differs per country: SMS needs a registration (DLT in India) and email does
 * not. The API returns them in the order to show them — the door that can
 * actually deliver a code leads.
 *
 * Nothing here opens a popup: the social buttons ask the API for the
 * provider's address and navigate to it. A popup is blocked by default in
 * several browsers and always in the stricter mobile ones, and the person it
 * fails for is the one least able to work out why.
 *
 * ── Why unavailable methods are still shown ────────────────────────────────
 * Somebody who signs in with Google everywhere will otherwise assume this app
 * cannot, and go hunting for a password field that does not exist. One grey
 * row answers the question.
 */
interface Props {
  /** What this sign-in leads to. Different for each console. */
  title: string;
  subtitle: string;
  onSignedIn: (result?: unknown) => void;
}

type Stage = 'choose' | 'phone' | 'phone-code' | 'email' | 'email-code';

const GOOGLE_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.5h6.5a5.6 5.6 0 0 1-2.4 3.7v3h3.9c2.3-2.1 3.5-5.2 3.5-8.9z"/>
    <path fill="#34A853" d="M12 24c3.2 0 5.9-1.1 7.9-2.9l-3.9-3c-1.1.7-2.4 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1A12 12 0 0 0 12 24z"/>
    <path fill="#FBBC05" d="M5.4 14.4a7.2 7.2 0 0 1 0-4.6V6.7H1.4a12 12 0 0 0 0 10.8l4-3.1z"/>
    <path fill="#EA4335" d="M12 4.8c1.8 0 3.3.6 4.6 1.8l3.4-3.4A12 12 0 0 0 1.4 6.7l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"/>
  </svg>
);

const APPLE_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor">
    <path d="M16.4 12.7c0-2.5 2-3.7 2.1-3.8-1.2-1.7-3-1.9-3.6-2-1.5-.2-3 .9-3.8.9s-2-.9-3.3-.8c-1.7 0-3.3 1-4.2 2.5-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.5 1.3-.1 1.8-.8 3.3-.8s2 .8 3.3.8c1.4 0 2.2-1.2 3.1-2.5.6-.9 1-1.8 1.3-2.8-2.7-1-2.7-4-2.7-4.2zM14 4.6c.7-.8 1.1-2 1-3.1-1 0-2.2.7-2.9 1.5-.6.7-1.2 1.9-1 3 1.1.1 2.2-.6 2.9-1.4z"/>
  </svg>
);

const PHONE_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="6" y="2" width="12" height="20" rx="2.5"/><path d="M11 18h2"/>
  </svg>
);

const EMAIL_ICON = (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m3.5 7 8.5 6 8.5-6"/>
  </svg>
);

const field =
  'min-h-14 w-full rounded-xl border border-(--line) bg-(--bg) px-4 text-lg outline-none focus:border-(--accent)';
const primary = 'min-h-14 w-full rounded-xl bg-(--accent) font-medium text-white disabled:opacity-40';
const secondary = 'mt-2 min-h-11 w-full text-xs text-(--muted) hover:text-(--fg)';

/** Same-origin: each console proxies /api under its own hostname. */
async function post(path: string, body: unknown): Promise<{ ok: boolean; status: number; data: unknown }> {
  const r = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => null);
  return { ok: r.ok, status: r.status, data };
}

/**
 * A suspended account (apps/api/src/suspension.ts) is refused at every door
 * with Better Auth's `ACCOUNT_SUSPENDED` and the sentence to show.
 */
export const SUSPENDED_TEXT = 'This account is suspended. Contact hello@drbell.app.';
export function refusedText(data: unknown, otherwise: string): string {
  const d = data as { code?: unknown; message?: unknown } | null;
  if (d?.code === 'ACCOUNT_SUSPENDED') return typeof d.message === 'string' ? d.message : SUSPENDED_TEXT;
  return otherwise;
}

export function SignIn({ title, subtitle, onSignedIn }: Props) {
  const [stage, setStage] = useState<Stage>('choose');
  const [phone, setPhone] = useState('+91');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [methods, setMethods] = useState<string[] | null>(null);

  useEffect(() => {
    fetch('/api/auth-methods')
      .then((r) => (r.ok ? (r.json() as Promise<{ methods?: string[] }>) : { methods: [] }))
      .then((d) => setMethods(d.methods ?? []))
      // Phone stands in when this call fails: a sign-in page with no way in
      // is every user locked out of their own reminders.
      .catch(() => setMethods(['phone']));
  }, []);

  const has = (id: string) => (methods ?? ['phone']).includes(id);
  const ownMethods = (methods ?? ['phone']).filter((m) => m === 'phone' || m === 'email');

  const sendPhone = async () => {
    setBusy(true);
    setError(null);
    const { ok, status } = await post('/api/auth/phone-number/send-otp', { phoneNumber: phone });
    setBusy(false);
    if (ok) return setStage('phone-code');
    setError(status === 429
      ? 'Too many codes requested. Wait a minute and try again.'
      : 'Could not send a code. Check the number and try again.');
  };

  const verifyPhone = async () => {
    setBusy(true);
    setError(null);
    const { ok, data } = await post('/api/auth/phone-number/verify', { phoneNumber: phone, code });
    setBusy(false);
    if (ok) return onSignedIn(data);
    setError(refusedText(data, 'That code did not work. Check it, or ask for a new one.'));
  };

  const sendEmail = async () => {
    setBusy(true);
    setError(null);
    const { ok, status } = await post('/api/auth/email-otp/send-verification-otp', {
      email: email.trim(),
      type: 'sign-in',
    });
    setBusy(false);
    if (ok) return setStage('email-code');
    setError(status === 429
      ? 'Too many codes requested. Wait a minute and try again.'
      : 'Could not send a code. Check the address and try again.');
  };

  const verifyEmail = async () => {
    setBusy(true);
    setError(null);
    const { ok, data } = await post('/api/auth/sign-in/email-otp', { email: email.trim(), otp: code });
    setBusy(false);
    if (ok) return onSignedIn(data);
    setError(refusedText(data, 'That code did not work. Check it, or ask for a new one.'));
  };

  const social = [
    { id: 'google', label: 'Continue with Google', icon: GOOGLE_ICON },
    { id: 'apple', label: 'Continue with Apple', icon: APPLE_ICON },
  ];

  /** The doors we own, in the order the API gave them. */
  const door = (id: string, index: number): ReactNode => {
    const lead = index === 0;
    const props = {
      onClick: () => { setStage(id as Stage); setError(null); },
      className: lead
        ? `flex ${primary} items-center justify-center gap-3 px-4`
        : 'mt-2 flex min-h-14 w-full items-center justify-center gap-3 rounded-xl border border-(--line) bg-(--bg) px-4 text-sm font-medium hover:border-(--accent)',
    };
    return id === 'phone'
      // The key passed on its own: React warns about a key spread in with the props.
      ? <button key={id} {...props}>{PHONE_ICON}Continue with your phone number</button>
      : <button key={id} {...props}>{EMAIL_ICON}Continue with your email</button>;
  };

  return (
    <div className="min-h-screen bg-(--bg)">
      <header className="mx-auto flex max-w-md items-center gap-3 px-4 pt-6">
        <Wordmark className="h-11 w-auto" />
        <ThemeToggle className="ml-auto" />
      </header>

      <main className="mx-auto w-full max-w-md px-4 pb-16 pt-8">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        <p className="mt-1 text-sm text-(--muted)">{subtitle}</p>

        <div className="mt-6 rounded-2xl border border-(--line) bg-(--panel) p-5">
          {stage === 'choose' && (
            <>
              {ownMethods.map(door)}
              <p className="mt-2 text-center text-xs text-(--muted)">
                New here? The same button creates your account. No password, ever.
              </p>

              {methods === null ? (
                <p className="mt-5 text-center text-xs text-(--muted)">Loading other options…</p>
              ) : (
                <>
                  <div className="my-5 flex items-center gap-3">
                    <span className="h-px flex-1 bg-(--line)" />
                    <span className="text-xs text-(--muted)">or</span>
                    <span className="h-px flex-1 bg-(--line)" />
                  </div>

                  <div className="space-y-2">
                    {social.map((m) =>
                      has(m.id) ? (
                        <SocialButton key={m.id} id={m.id} label={m.label} icon={m.icon} />
                      ) : (
                        <div
                          key={m.id}
                          aria-disabled="true"
                          className="flex min-h-14 w-full items-center justify-center gap-3 rounded-xl border border-(--line) px-4 text-sm text-(--muted) opacity-60"
                        >
                          <span className="grayscale">{m.icon}</span>
                          {m.label.replace('Continue with ', '')}
                          <span className="rounded bg-(--line) px-2 py-0.5 text-[10px] font-semibold tracking-wide">
                            COMING SOON
                          </span>
                        </div>
                      ),
                    )}
                  </div>

                  <p className="mt-4 text-center text-xs text-(--muted)">
                    {/* Worth saying plainly: people are right to be wary of a
                        health app asking to use their Google account. */}
                    We never see your Google or Apple password.
                  </p>
                </>
              )}
            </>
          )}

          {stage === 'phone' && (
            <>
              <label htmlFor="phone" className="mb-1 block text-xs font-medium text-(--muted)">
                Your mobile number
              </label>
              <input
                id="phone" value={phone} autoFocus inputMode="tel" autoComplete="tel"
                placeholder="+919000000000" className={`mb-3 ${field}`}
                onChange={(e) => setPhone(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void sendPhone()}
              />
              <button disabled={busy || !/^\+[1-9]\d{7,14}$/.test(phone)} onClick={() => void sendPhone()} className={primary}>
                {busy ? 'Sending…' : 'Send me a code'}
              </button>
              <button onClick={() => { setStage('choose'); setError(null); }} className={secondary}>
                ← Other ways to sign in
              </button>
            </>
          )}

          {stage === 'email' && (
            <>
              <label htmlFor="email" className="mb-1 block text-xs font-medium text-(--muted)">
                Your email address
              </label>
              <input
                id="email" value={email} autoFocus inputMode="email" autoComplete="email" type="email"
                placeholder="you@example.com" className={`mb-3 ${field}`}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void sendEmail()}
              />
              <button disabled={busy || !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(email.trim())} onClick={() => void sendEmail()} className={primary}>
                {busy ? 'Sending…' : 'Send me a code'}
              </button>
              <button onClick={() => { setStage('choose'); setError(null); }} className={secondary}>
                ← Other ways to sign in
              </button>
            </>
          )}

          {(stage === 'phone-code' || stage === 'email-code') && (
            <>
              <label htmlFor="code" className="mb-1 block text-xs font-medium text-(--muted)">
                6-digit code sent to {stage === 'phone-code' ? phone : email.trim()}
              </label>
              <input
                id="code" value={code} autoFocus inputMode="numeric" autoComplete="one-time-code"
                className="mb-3 min-h-14 w-full rounded-xl border border-(--line) bg-(--bg) px-4 text-center text-2xl tracking-[0.4em] outline-none focus:border-(--accent)"
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={(e) => e.key === 'Enter' && void (stage === 'phone-code' ? verifyPhone() : verifyEmail())}
              />
              <button
                disabled={busy || code.length !== 6}
                onClick={() => void (stage === 'phone-code' ? verifyPhone() : verifyEmail())}
                className={primary}
              >
                {busy ? 'Checking…' : 'Sign in'}
              </button>
              <button
                onClick={() => { setStage(stage === 'phone-code' ? 'phone' : 'email'); setCode(''); setError(null); }}
                className={secondary}
              >
                {stage === 'phone-code' ? 'Use a different number' : 'Use a different address'}
              </button>
            </>
          )}

          {error && (
            <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
              {error}
            </p>
          )}
        </div>

        <p className="mt-5 text-center text-xs text-(--muted)">
          By continuing you agree to our{' '}
          <a className="text-(--accent) underline" href="https://drbell.app/legal/terms">Terms of Use</a>{' '}
          and{' '}
          <a className="text-(--accent) underline" href="https://drbell.app/legal/privacy">Privacy Policy</a>.
        </p>
      </main>
    </div>
  );
}

/**
 * Ask the API where to send the person, then go there.
 *
 * Not a form post: the endpoint answers 200 with JSON and a Location header,
 * which browsers do not follow on a 200 — a form would have shown the JSON.
 */
function SocialButton({ id, label, icon }: { id: string; label: string; icon: ReactNode }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const start = async () => {
    setBusy(true);
    setFailed(null);
    const { ok, data } = await post('/api/auth/sign-in/social', { provider: id, callbackURL: '/' });
    const url = (data as { url?: string } | null)?.url;
    if (ok && url) {
      window.location.assign(url);
      return;
    }
    setBusy(false);
    setFailed((data as { message?: string } | null)?.message ?? 'That sign-in method is not available right now.');
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void start()}
        disabled={busy}
        className="flex min-h-14 w-full items-center justify-center gap-3 rounded-xl border border-(--line) bg-(--bg) px-4 text-sm font-medium hover:border-(--accent) disabled:opacity-50"
      >
        {icon}
        {busy ? 'Redirecting…' : label}
      </button>
      {failed && <p role="alert" className="text-sm text-red-500">{failed}</p>}
    </>
  );
}
